import {
    Box,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    VStack,
    Flex,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    HStack,
    Text,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useToast,
    Button,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@chakra-ui/react';
import axios from 'axios';
import { SelectionTab, SelectionTabKey, SelectionPart, SupplierNote, OrderDetailResponse } from '../../hooks/useGetOrderDetail';
import { SOURCE_MARKETPLACE_CONFIG } from '../SourceBadge';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { TbMessage, TbFileExport, TbMessagePlus, TbChecks } from 'react-icons/tb';
import { FiShoppingCart } from 'react-icons/fi';
import { formatPrice, formatPercentage } from '../../functions';
import { useState, useMemo, useEffect, useContext } from 'react';
import PartOptionsModal from '../../modals/PartOptionsModal';
import PartOptionNoteModal from '../../modals/PartOptionNoteModal';
import { useTogglePartExclusion } from '../../hooks/useTogglePartExclusion';
import PartType from '../PartType';
import ListPrice from '../ListPrice';
import PlaceOrderConfirmationModal from '../../modals/PlaceOrderConfirmationModal';
import SubmitQuoteModal from '../../modals/SubmitQuoteModal';
import { useExportSelections } from '../../hooks/useExportSelections';
import { useRefetchQuotes } from '../../hooks/useRefetchQuotes';
import { useCancelScheduledOrderPlacement } from '../../hooks/useCancelScheduledOrderPlacement';
import { useConfirmAllSelections } from '../../hooks/useConfirmAllSelections';
import { useMarkPartOrdered } from '../../hooks/useMarkPartOrdered';
// import { useAttemptPartNumberChange } from '../../hooks/useAttemptPartNumberChange';
import PartNumber from '../PartNumber';
import SortArrowIcon from '../SortArrowIcon';
import SelectionStatusIcon from '../SelectionStatusIcon';
import PartBayButton from '../PartBayButton';
import CollapsibleSearch from '../CollapsibleSearch';
import ResetToPartBaySelectionsIcon from '../ResetToPartBaySelectionsIcon';
import ResetSelectionsConfirmationModal from '../../modals/ResetSelectionsConfirmationModal';
import { brandCopy } from '../../constants/brand';
import SelectionChangeIndicators, {
    getActionColumnMinWidth,
    getActionColumnSlotCount,
    getMaxIndicatorSlotCount,
} from './SelectionChangeIndicators';
import SessionContext from '../../contexts/SessionContext';
import { relocateAutoExcludedDuplicates } from './relocateAutoExcludedDuplicates';
import SelectionPartActionsMenu from './SelectionPartActionsMenu';


interface SelectionProps {
    sectionKey: SelectionTabKey;
    parts: Array<SelectionPart>;
    orderId: string;
    vin: string;
    refetch: () => void | Promise<unknown>;
    filter: FilterType;
    isEvOrder: boolean;
    canAttemptPnChange: boolean;
    quotesOnly: boolean;
    potentialTotal: boolean;
    searchQuery?: string;
    drpJob?: boolean;
}

type SortField = 'listPrice' | 'buyPrice' | 'grossMargin';
type SortDirection = 'asc' | 'desc';

interface SortState {
    field: SortField | null;
    direction: SortDirection;
}

const Selection = ({
    parts,
    orderId,
    vin,
    refetch,
    sectionKey,
    filter,
    isEvOrder,
    canAttemptPnChange,
    quotesOnly,
    potentialTotal,
    searchQuery = '',
    drpJob = false,
}: SelectionProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPartId, setSelectedPartId] = useState<string | null>(null);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedPartForNote, setSelectedPartForNote] = useState<SelectionPart | null>(null);
    const [isSubmitQuoteModalOpen, setIsSubmitQuoteModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const toggleExclusion = useTogglePartExclusion();
    const markPartOrdered = useMarkPartOrdered();
    const exportSelections = useExportSelections();
    const confirmAllSelections = useConfirmAllSelections();
    // const attemptPnChange = useAttemptPartNumberChange();
    const toast = useToast();
    const [sortState, setSortState] = useState<SortState>({ field: null, direction: 'asc' });
    const isQuoteWorkflow = quotesOnly || potentialTotal;
    const sessionContext = useContext(SessionContext);
    const allowPlaceOrder = (isEvOrder && sessionContext?.permissions?.includes('place-ev-order')) || sessionContext?.permissions?.includes('place-order');

    const searchFilteredParts = useMemo(() => {
        if (!searchQuery.trim()) return parts;
        const q = searchQuery.toLowerCase().trim();
        return parts.filter((part) =>
            part.name.toLowerCase().includes(q) ||
            (part.partNumber && part.partNumber.toLowerCase().includes(q)) ||
            (part.correctedPartNumber && part.correctedPartNumber.toLowerCase().includes(q)) ||
            (part.vendor && part.vendor.toLowerCase().includes(q)) ||
            (part.delivery && part.delivery.toLowerCase().includes(q)) ||
            (part.type && part.type.toLowerCase().includes(q)) ||
            (part.notes && part.notes.toLowerCase().includes(q))
        );
    }, [parts, searchQuery]);

    const filteredParts = useMemo(() => {
        if (filter === 'estimate') {
            return [...searchFilteredParts];
        }

        return [...searchFilteredParts].sort((a, b) => {
            const aMatches = (() => {
                switch (filter) {
                    case 'flagged': return a.flag;
                    case 'excluded': return a.excludeOrdering;
                    default: return false;
                }
            })();

            const bMatches = (() => {
                switch (filter) {
                    case 'flagged': return b.flag;
                    case 'excluded': return b.excludeOrdering;
                    default: return false;
                }
            })();

            if (aMatches && !bMatches) return -1;
            if (!aMatches && bMatches) return 1;
            return 0;
        });
    }, [searchFilteredParts, filter]);

    const sortedParts = useMemo(() => {
        if (!sortState.field) {
            return filteredParts;
        }

        const partsToSort = [...filteredParts];

        partsToSort.sort((a, b) => {
            const getValue = (part: SelectionPart) => {
                switch (sortState.field) {
                    case 'listPrice': return part.listPrice ?? null;
                    case 'buyPrice': return part.buyPrice ?? null;
                    case 'grossMargin': return part.grossMargin ?? null;
                    default: return null;
                }
            };

            const aValue = getValue(a);
            const bValue = getValue(b);

            if (aValue === null || aValue === undefined) {
                return bValue === null || bValue === undefined ? 0 : (sortState.direction === 'asc' ? 1 : -1);
            }

            if (bValue === null || bValue === undefined) {
                return sortState.direction === 'asc' ? -1 : 1;
            }

            if (aValue === bValue) return 0;

            return sortState.direction === 'asc' ? aValue - bValue : bValue - aValue;
        });

        return partsToSort;
    }, [filteredParts, sortState]);

    const maxIndicatorSlots = useMemo(
        () => getMaxIndicatorSlotCount(sortedParts),
        [sortedParts]
    );

    const actionColumnMinW = useMemo(
        () => getActionColumnMinWidth(getActionColumnSlotCount(sortedParts)),
        [sortedParts]
    );

    const toggleSort = (field: SortField) => {
        setSortState((prev) => {
            if (prev.field !== field) {
                return { field, direction: 'asc' };
            }

            if (prev.direction === 'asc') {
                return { field, direction: 'desc' };
            }

            return { field: null, direction: 'asc' };
        });
    };

    const renderSortableHeader = (label: string, field: SortField) => {
        const isActive = sortState.field === field;
        const direction = isActive ? sortState.direction : 'none';

        return (
            <Flex
                as="button"
                type="button"
                align="center"
                justify="flex-end"
                gap="6px"
                w="100%"
                color={isActive ? "#005BC4" : "gray.600"}
                fontSize="xs"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="0.05em"
                cursor="pointer"
                onClick={(event) => {
                    event.stopPropagation();
                    toggleSort(field);
                }}
            >
                <Text as="span">
                    {label}
                </Text>
                <Box lineHeight={0} color={isActive ? "#005BC4" : "gray.400"}>
                    <SortArrowIcon direction={direction} />
                </Box>
            </Flex>
        );
    };

    const handleRowClick = (partId: string, isAssembly: boolean) => {
        if (isAssembly) return;
        setSelectedPartId(partId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPartId(null);
    };

    const handlePlaceOrderClick = () => {
        setIsConfirmationOpen(true);
    };

    const handleExportSelections = async () => {
        try {
            await exportSelections.mutateAsync({ orderId });
            toast({
                title: "Selections export queued!",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Failed to export selections",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };


    const handleConfirmAllSelections = async () => {
        try {
            await confirmAllSelections.mutateAsync({ orderId });
            toast({
                title: "All selections confirmed",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Failed to confirm selections",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    // const handleAttemptPnChange = () => {
    //     attemptPnChange.mutate({ orderId }, {
    //         onSuccess: () => {
    //             toast({
    //                 title: "Part number change requested",
    //                 description: "A part number change attempt has been queued. This may take a few minutes.",
    //                 status: "success",
    //                 duration: 5000,
    //                 isClosable: true,
    //             });
    //         },
    //         onError: (error: any) => {
    //             if (error?.response?.status === 409) {
    //                 toast({
    //                     title: "Unable to attempt part number change",
    //                     description: "A part number change has already been attempted for these parts.",
    //                     status: "warning",
    //                     duration: 5000,
    //                     isClosable: true,
    //                 });
    //             } else {
    //                 toast({
    //                     title: "Cannot attempt part number change",
    //                     status: "error",
    //                     duration: 5000,
    //                     isClosable: true,
    //                 });
    //             }
    //         }
    //     });
    // };

    const handleCloseConfirmation = () => {
        setIsConfirmationOpen(false);
    };

    const handleNoteClick = (part: SelectionPart) => {
        setSelectedPartForNote(part);
        setIsNoteModalOpen(true);
    };

    const handleNoteModalClose = () => {
        setIsNoteModalOpen(false);
        setSelectedPartForNote(null);
    };

    const handleExcludeToggle = async (partId: string, isAssembly: boolean) => {
        toggleExclusion.mutate({ orderPartId: partId, orderId: orderId, origin: 'ordering', isAssembly });
    };

    const handleMarkOrderedToggle = (partId: string) => {
        markPartOrdered.mutate({ orderId, orderPartId: partId });
    };

    return (
        <>
            {/* Header */}
            <Flex justify="space-between" align="center" px={6} pt={5} pb={4}>
                <Heading size="md" color="gray.800">
                    {
                        sectionKey === "pending" ? "Pending Parts" :
                            sectionKey === "ordered" ? "Ordered Parts" :
                                sectionKey === "excluded" ? "Excluded Parts" :
                                    sectionKey === "no-quoted" ? "No Quoted Parts" : "All Parts"}
                </Heading>
                {sectionKey === 'pending' && (
                    <HStack spacing={3}>
                        <Menu placement="bottom-end">
                            <MenuButton
                                as={Button}
                                size="sm"
                                variant="outline"
                                colorScheme="gray"
                                bg="white"
                                rightIcon={<ChevronDownIcon />}
                                _hover={{ bg: 'gray.50' }}
                                _active={{ bg: 'gray.100' }}
                                _focus={{ boxShadow: 'none' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {isQuoteWorkflow || allowPlaceOrder ? 'More Actions' : 'Actions'}
                            </MenuButton>
                            <MenuList py={1} minW="200px">
                                {!isQuoteWorkflow && (
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleExportSelections();
                                        }}
                                        isDisabled={exportSelections.isPending}
                                        fontSize="sm"
                                        px={3}
                                        py={2}
                                        color="gray.700"
                                        _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                    >
                                        <HStack spacing={2}>
                                            <Icon as={TbFileExport as React.ElementType} boxSize={4} color="gray.500" />
                                            <Text>{exportSelections.isPending ? 'Exporting...' : 'Export Selections'}</Text>
                                        </HStack>
                                    </MenuItem>
                                )}
                                <MenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleConfirmAllSelections();
                                    }}
                                    isDisabled={confirmAllSelections.isPending}
                                    fontSize="sm"
                                    px={3}
                                    py={2}
                                    color="gray.700"
                                    _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                >
                                    <HStack spacing={2}>
                                        <Icon as={TbChecks as React.ElementType} boxSize={4} color="gray.500" />
                                        <Text>{confirmAllSelections.isPending ? 'Confirming...' : 'Confirm All Selections'}</Text>
                                    </HStack>
                                </MenuItem>
                                {/* {canAttemptPnChange && (
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAttemptPnChange();
                                        }}
                                        isDisabled={attemptPnChange.isPending}
                                        fontSize="sm"
                                        px={3}
                                        py={2}
                                        color="gray.700"
                                        _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                    >
                                        <HStack spacing={2}>
                                            <Icon as={TbReplace as React.ElementType} boxSize={4} color="gray.500" />
                                            <Text>{attemptPnChange.isPending ? 'Requesting...' : 'Attempt Part Number Change'}</Text>
                                        </HStack>
                                    </MenuItem>
                                )} */}
                                {sessionContext?.permissions?.includes('change-selections') && (
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsResetModalOpen(true);
                                        }}
                                        fontSize="sm"
                                        px={3}
                                        py={2}
                                        color="gray.700"
                                        _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                    >
                                        <HStack spacing={2}>
                                            <Box as="span" display="inline-flex" color="gray.500">
                                                <ResetToPartBaySelectionsIcon size={16} color="currentColor" />
                                            </Box>
                                            <Text>{brandCopy('Reset to PartBay Selections')}</Text>
                                        </HStack>
                                    </MenuItem>
                                )}
                                {!isEvOrder && sessionContext?.permissions?.includes('request-quotes') && (
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsSubmitQuoteModalOpen(true);
                                        }}
                                        fontSize="sm"
                                        px={3}
                                        py={2}
                                        color="gray.700"
                                        _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                    >
                                        <HStack spacing={2}>
                                            <Icon as={TbMessagePlus as React.ElementType} boxSize={4} color="gray.500" />
                                            <Text>Request More Quotes</Text>
                                        </HStack>
                                    </MenuItem>
                                )}
                                {isQuoteWorkflow && allowPlaceOrder && (
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePlaceOrderClick();
                                        }}
                                        isDisabled={isModalOpen || toggleExclusion.isPending}
                                        fontSize="sm"
                                        px={3}
                                        py={2}
                                        color="gray.700"
                                        _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                    >
                                        <HStack spacing={2}>
                                            <Icon as={FiShoppingCart as React.ElementType} boxSize={4} color="gray.500" />
                                            <Text>Place Order</Text>
                                        </HStack>
                                    </MenuItem>
                                )}
                            </MenuList>
                        </Menu>
                        {isQuoteWorkflow && (
                            <PartBayButton
                                variant="outline"
                                size="sm"
                                colorScheme="partbay-blue"
                                text="Export Selections"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void handleExportSelections();
                                }}
                                isLoading={exportSelections.isPending}
                                loadingText="Exporting..."
                            />
                        )}
                        {!isQuoteWorkflow && allowPlaceOrder && (
                            <PartBayButton
                                variant='solid'
                                size='sm'
                                onClick={handlePlaceOrderClick}
                                text='Place Order'
                                colorScheme='partbay-blue'
                                isDisabled={isModalOpen || toggleExclusion.isPending}
                            />
                        )}
                    </HStack>
                )}
                {sectionKey === 'no-quoted' && !isEvOrder &&
                    <PartBayButton
                        variant='outline'
                        size='sm'
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsSubmitQuoteModalOpen(true);
                        }}
                        text='Request More Quotes'
                        colorScheme='partbay-blue'
                        isDisabled={isSubmitQuoteModalOpen}
                    />
                }
            </Flex>

            {/* Table */}
            <Box borderTop="1px solid" borderColor="gray.200" overflowX="auto">
                <Table variant="simple" size="sm" w="100%" minW="1080px" sx={{ 'th:first-of-type, td:first-of-type': { pl: 6 }, 'th:last-of-type, td:last-of-type': { pr: 6 } }}>
                    <Thead>
                        <Tr bg="gray.50">
                            <Th w="50px" minW="50px" py={2}></Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="180px">
                                PART NAME
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="140px">
                                PART NUMBER
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="150px">
                                VENDOR NAME
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px">
                                DELIVERY TIME
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="80px" isNumeric>
                                QUANTITY
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px" isNumeric>
                                {renderSortableHeader('LIST PRICE', 'listPrice')}
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px" isNumeric>
                                {renderSortableHeader('BUY PRICE', 'buyPrice')}
                            </Th>
                            <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="120px" isNumeric>
                                {renderSortableHeader('GROSS MARGIN', 'grossMargin')}
                            </Th>
                            <Th py={2} minW={actionColumnMinW} w={actionColumnMinW}></Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {sortedParts.length === 0 ? (
                            <Tr>
                                <Td colSpan={10} py={12} textAlign="center">
                                    <Text fontSize="lg" color="gray.600" fontWeight="medium">
                                        No parts in this section
                                    </Text>
                                </Td>
                            </Tr>
                        ) : (
                            sortedParts.map((part) => (
                                <Tr
                                    key={part.partId}
                                    _hover={{
                                        bg: part.selectionStatus === 'manual' ? 'yellow.100' : part.selectionStatus === 'excluded' ? 'red.100' : part.selectionStatus === 'flagged' ? 'orange.100' : part.selectionStatus === 'ordered' ? 'green.100' : 'gray.50',
                                        cursor: "pointer"
                                    }}
                                    onClick={() => handleRowClick(part.partId, part.isAssembly)}
                                    bg={part.selectionStatus === 'manual' ? 'yellow.50' : part.selectionStatus === 'excluded' ? 'red.50' : part.selectionStatus === 'flagged' ? 'orange.50' : part.selectionStatus === 'ordered' ? 'green.50' : 'transparent'}
                                // boxShadow={sectionKey !== 'selection' ? 'none' : part.excludeOrdering ? 'inset 4px 0 0 0 rgb(221, 32, 32)' : part.flag ? 'inset 4px 0 0 0 #DD6B20' : 'none'}
                                >
                                    <Td py={4} w="50px" textAlign="center">
                                        <SelectionStatusIcon
                                            status={part.selectionStatus}
                                            flagReason={part.flagReason}
                                        />
                                    </Td>
                                    <Td py={4} maxW='240px'>
                                        <VStack align="flex-start" spacing={1}>
                                            <Text color="gray.900" fontWeight="medium" fontSize="sm" whiteSpace="pre-line">
                                                {part.name}
                                            </Text>
                                            {part.type ?
                                                <PartType
                                                    partType={part.type}
                                                /> : null
                                            }
                                        </VStack>
                                    </Td>
                                    <Td py={4}>
                                        {!part.isAssembly ?
                                            <PartNumber
                                                partNumber={part.partNumber}
                                                correctedPartNumber={part.correctedPartNumber}
                                                vin={vin}

                                            />
                                            :
                                            <Text color="gray.900" fontSize="sm">
                                                —
                                            </Text>
                                        }
                                    </Td>
                                    <Td py={4}>
                                        <Text color="gray.900" fontSize="sm">
                                            {part.vendor || '-'}
                                        </Text>
                                    </Td>
                                    <Td py={4}>
                                        <Text color="gray.900" fontSize="sm">
                                            {part.delivery || '-'}
                                        </Text>
                                    </Td>
                                    <Td py={4} isNumeric>
                                        <Text color="gray.900" fontSize="sm">
                                            {part.quantity || 1}
                                        </Text>
                                    </Td>
                                    <Td py={4} isNumeric>
                                        <ListPrice
                                            listPriceAdjusted={part.listPriceAdjusted}
                                            listPrice={part.listPrice}
                                        />
                                    </Td>
                                    <Td py={4} isNumeric>
                                        <Text color="gray.900" fontSize="sm" fontWeight="medium">
                                            {part.buyPrice ? formatPrice(part.buyPrice) : '-'}
                                        </Text>
                                    </Td>
                                    <Td py={4} isNumeric>
                                        <Text color="gray.900" fontSize="sm">
                                            {formatPercentage(part.grossMargin)}
                                        </Text>
                                    </Td>
                                    <Td py={4} minW={actionColumnMinW} w={actionColumnMinW} onClick={(e) => e.stopPropagation()}>
                                        <VStack spacing={1} align="center">
                                            <HStack spacing={2} justify="flex-end" w="100%">
                                                <SelectionChangeIndicators
                                                    indicators={part.changeIndicators}
                                                    reservedIndicatorSlots={maxIndicatorSlots}
                                                />
                                                <Box w={8} h={8} flexShrink={0}>
                                                    {(sessionContext?.permissions?.includes('add-option-note') || sectionKey === 'ordered') && (
                                                        <IconButton
                                                            aria-label="Add Note"
                                                            icon={<Icon as={TbMessage as React.ElementType} w={4} h={4} />}
                                                            size="sm"
                                                            variant="ghost"
                                                            flexShrink={0}
                                                            w={8}
                                                            h={8}
                                                            color={part.notes ? "#0071E3" : "gray.400"}
                                                            bg={part.notes && sectionKey === 'pending' ? "blue.50" : "transparent"}
                                                            onClick={() => handleNoteClick(part)}
                                                            _hover={{
                                                                bg: part.notes && sectionKey === 'pending' ? "blue.100" : "transparent",
                                                                color: part.notes ? "#0071E3" : "gray.700"
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                <Box w={8} h={8} flexShrink={0}>
                                                    <SelectionPartActionsMenu
                                                        phaseKey={sectionKey}
                                                        part={part}
                                                        isEvOrder={isEvOrder}
                                                        onExclude={() => handleExcludeToggle(part.partId, part.isAssembly)}
                                                        onMarkOrdered={() => handleMarkOrderedToggle(part.partId)}
                                                    />
                                                </Box>
                                            </HStack>
                                        </VStack>
                                    </Td>
                                </Tr>
                            ))
                        )}
                    </Tbody>
                </Table>
            </Box>
            {selectedPartId &&
                <PartOptionsModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    orderPartId={selectedPartId}
                    orderId={orderId}
                    source='order'
                    drpJob={drpJob}
                />}
            <PlaceOrderConfirmationModal
                isOpen={isConfirmationOpen}
                onClose={handleCloseConfirmation}
                orderId={orderId}
            />
            {selectedPartForNote !== null &&
                <PartOptionNoteModal
                    key={selectedPartForNote.partId}
                    isOpen={isNoteModalOpen}
                    onClose={handleNoteModalClose}
                    selectedOptionId={selectedPartForNote.selectedOptionId || ''}
                    orderId={orderId}
                    orderPartId={selectedPartForNote.partId}
                    notes={selectedPartForNote.notes || ''}
                    estimatingNote={selectedPartForNote.estimatingNotes || ''}
                    listPriceAdjusted={selectedPartForNote.listPriceAdjusted || false}
                    refetch={refetch}
                    isEditable={sectionKey === 'pending' && !!sessionContext?.permissions?.includes('add-option-note')}
                    partType={selectedPartForNote.type}
                    isAssembly={selectedPartForNote.isAssembly}
                    drpJob={drpJob}
                />}
            <SubmitQuoteModal
                isOpen={isSubmitQuoteModalOpen}
                onClose={() => setIsSubmitQuoteModalOpen(false)}
                orderId={orderId}
                resubmit
            />
            <ResetSelectionsConfirmationModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                orderId={orderId}
            />
        </>
    );
};

interface SelectionTabsProps {
    tabs: Array<SelectionTab>;
    orderId: string;
    vin: string;
    refetch: () => void | Promise<unknown>;
    potentialTotal: boolean;
    quotesOnly: boolean;
    preOrderBackorder?: boolean;
    needsSourcePartsMessage?: string | null;
    quoteRequestErrors?: OrderDetailResponse['quoteRequestErrors'];
    quoteRequestAllowedAutoOrder?: boolean;
    supplierNotes?: Array<SupplierNote>;
    isEvOrder: boolean;
    canAttemptPnChange: boolean;
    oemCertificationPriceDifference?: number;
    scheduledOrderPlacement?: Date | string | null;
    drpJob?: boolean;
}

type FilterType = 'estimate' | 'flagged' | 'excluded';

const SelectionTabs = ({
    tabs,
    orderId,
    vin,
    refetch,
    potentialTotal,
    quotesOnly,
    preOrderBackorder = false,
    needsSourcePartsMessage = null,
    quoteRequestErrors,
    quoteRequestAllowedAutoOrder = false,
    isEvOrder,
    canAttemptPnChange,
    oemCertificationPriceDifference,
    scheduledOrderPlacement,
    drpJob = false,
}: SelectionTabsProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTabKey, setSelectedTabKey] = useState<SelectionTab['key'] | null>(null);
    const toast = useToast();
    const refetchQuotes = useRefetchQuotes();
    const cancelScheduledPlacement = useCancelScheduledOrderPlacement();

    const handleRecreateTeslaOrder = async () => {
        try {
            await refetchQuotes.mutateAsync({ orderId });
            await refetch();
            toast({
                title: "Tesla order recreation queued!",
                description: "This may take a few minutes. You'll be notified when it's ready.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            const status = axios.isAxiosError(error) ? error.response?.status : undefined;
            if (status === 409) {
                toast({
                    title: "Recreation already in progress",
                    description: "A Tesla order recreation is already running for this order.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Failed to recreate Tesla order",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        }
    };

    // const handleRefetchSourceParts = async () => {
    //     try {
    //         await refetchQuotes.mutateAsync({ orderId });
    //         await refetch();
    //         toast({
    //             title: "Source parts refetch queued!",
    //             description: "This may take a few minutes. You'll be notified when it's ready.",
    //             status: "success",
    //             duration: 5000,
    //             isClosable: true,
    //         });
    //     } catch (error) {
    //         const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    //         if (status === 409) {
    //             toast({
    //                 title: "Refetch already in progress",
    //                 description: "A source parts refetch is already running for this order.",
    //                 status: "warning",
    //                 duration: 5000,
    //                 isClosable: true,
    //             });
    //         } else {
    //             toast({
    //                 title: "Failed to refetch source parts",
    //                 status: "error",
    //                 duration: 5000,
    //                 isClosable: true,
    //             });
    //         }
    //     }
    // };

    const handleCancelScheduledPlacement = async () => {
        try {
            await cancelScheduledPlacement.mutateAsync({ orderId });
            await refetch();
            toast({
                title: "Scheduled placement cancelled",
                description: "This order will no longer be placed automatically.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            const message = axios.isAxiosError(error)
                ? error.response?.data?.message
                : undefined;
            toast({
                title: "Failed to cancel scheduled placement",
                description: typeof message === 'string' ? message : undefined,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const displayTabs = useMemo(
        () => relocateAutoExcludedDuplicates(tabs),
        [tabs],
    );

    const selectedTabIndex = useMemo(() => {
        if (!selectedTabKey || displayTabs.length === 0) return 0;
        const index = displayTabs.findIndex((tab) => tab.key === selectedTabKey);
        if (index >= 0) return index;
        const excludedIndex = displayTabs.findIndex((tab) => tab.key === 'excluded');
        return excludedIndex >= 0 ? excludedIndex : 0;
    }, [displayTabs, selectedTabKey]);

    useEffect(() => {
        if (!selectedTabKey || displayTabs.some((tab) => tab.key === selectedTabKey)) return;
        const fallback = displayTabs.find((tab) => tab.key === 'excluded') ?? displayTabs[0];
        if (fallback) setSelectedTabKey(fallback.key);
    }, [displayTabs, selectedTabKey]);

    if (displayTabs.length === 0) {
        return null;
    }

    const hasNoQuotedParts = displayTabs.some((selectionTab) => (
        selectionTab.key === 'no-quoted' && (selectionTab.total > 0 || selectionTab.parts.length > 0)
    ));

    const scheduledOrderPlacementLabel = (() => {
        if (!scheduledOrderPlacement) return null;
        const d = scheduledOrderPlacement instanceof Date ? scheduledOrderPlacement : new Date(scheduledOrderPlacement);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    })();

    return (
        <Box>
            <Tabs
                index={selectedTabIndex}
                onChange={(index) => {
                    const key = displayTabs[index]?.key;
                    if (key) setSelectedTabKey(key);
                }}
                variant="unstyled"
                colorScheme="blue"
            >
                <Flex justify="space-between" align="flex-end" mb={4} borderBottom="1.5px solid" borderColor="gray.200" mx={-6} px={6}>
                    <TabList border="none" gap={0}>
                        {displayTabs.map((tab) => (
                            <Tab
                                key={tab.key}
                                fontWeight="medium"
                                fontSize="sm"
                                color="gray.500"
                                bg="transparent"
                                border="none"
                                borderBottom="1.5px solid"
                                borderColor="transparent"
                                mb="-1.5px"
                                py={2}
                                px={3}
                                _selected={{
                                    color: "gray.800",
                                    borderColor: "gray.500",
                                }}
                                _hover={{
                                    color: "gray.700",
                                }}
                            >
                                <HStack spacing={0}>
                                    <Text>{tab.title}</Text>
                                    {tab.total > 0 && (
                                        <Flex
                                            ml={1.5}
                                            bg="gray.100"
                                            borderRadius="lg"
                                            h="18px"
                                            minW="18px"
                                            px="5px"
                                            align="center"
                                            justify="center"
                                            flexShrink={0}
                                        >
                                            <Text
                                                fontSize="11px"
                                                fontWeight="bold"
                                                lineHeight="1"
                                                color="gray.800"
                                            >
                                                {tab.total}
                                            </Text>
                                        </Flex>
                                    )}
                                </HStack>
                            </Tab>
                        ))}
                    </TabList>
                    <Flex align="center" gap={3} pb={2}>
                        <CollapsibleSearch
                            isCompact={false}
                            width="350px"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </Flex>
                </Flex>

                <TabPanels>
                    {displayTabs.map((tab) => (
                        <TabPanel key={tab.key} px={0} py={0}>
                            <VStack spacing={4} align="stretch">
                                {tab.key === 'pending' && scheduledOrderPlacementLabel && (
                                    <Alert
                                        status="warning"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="yellow.300"
                                        bg="yellow.50"
                                    >
                                        <AlertIcon color="yellow.600" />
                                        <AlertDescription color="yellow.900" fontSize="sm">
                                            This order is scheduled to be placed on <strong>{scheduledOrderPlacementLabel}</strong>.
                                        </AlertDescription>
                                        <Button
                                            ml="auto"
                                            flexShrink={0}
                                            size="sm"
                                            colorScheme="red"
                                            variant="outline"
                                            onClick={handleCancelScheduledPlacement}
                                            isLoading={cancelScheduledPlacement.isPending}
                                            loadingText="Canceling..."
                                        >
                                            Cancel Schedule
                                        </Button>
                                    </Alert>
                                )}
                                {tab.key === 'pending' && isEvOrder && hasNoQuotedParts && (
                                    <Alert
                                        status="info"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="blue.200"
                                        bg="blue.50"
                                    >
                                        <AlertIcon color="blue.500" />
                                        <AlertDescription color="gray.700" fontSize="sm">
                                            Missing parts? You can try recreating this order in the Tesla EPC.
                                        </AlertDescription>
                                        <Button
                                            ml="auto"
                                            flexShrink={0}
                                            size="sm"
                                            colorScheme="blue"
                                            variant="outline"
                                            onClick={handleRecreateTeslaOrder}
                                            isLoading={refetchQuotes.isPending}
                                            loadingText="Recreating..."
                                        >
                                            Recreate in Tesla EPC
                                        </Button>
                                    </Alert>
                                )}
                                {tab.key === 'pending' && oemCertificationPriceDifference != null && (
                                    <Alert
                                        status="info"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="blue.200"
                                        bg="blue.50"
                                    >
                                        <AlertIcon color="blue.500" />
                                        <AlertDescription color="gray.700" fontSize="sm">
                                            OE parts are {formatPrice(oemCertificationPriceDifference)} more expensive for customer
                                        </AlertDescription>
                                    </Alert>
                                )}
                                {tab.key === 'pending' && preOrderBackorder && !potentialTotal && !quotesOnly && (
                                    <Alert
                                        status="error"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="red.400"
                                        bg="red.50"
                                    >
                                        <AlertIcon color="red.500" />
                                        <AlertDescription color="gray.700" fontSize="sm">
                                            Auto-ordering was disabled for this pre-order because one or more selected parts are backordered.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {tab.key === 'pending' && !isEvOrder && needsSourcePartsMessage != null && (
                                    <Alert
                                        status="error"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="red.400"
                                        bg="red.50"
                                    >
                                        <AlertIcon color="red.500" />
                                        <AlertDescription color="gray.700" fontSize="sm">
                                            {needsSourcePartsMessage || 'Auto-ordering was disabled because we were unable to fetch source parts and are waiting for someone to review before proceeding.'}
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {tab.key === 'pending' && !!quoteRequestErrors?.length && (
                                    <Alert
                                        status="error"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="red.400"
                                        bg="red.50"
                                        alignItems="flex-start"
                                    >
                                        <AlertIcon color="red.500" />
                                        <Box>
                                            <AlertDescription color="gray.700" fontSize="sm">
                                                Receiving quotes from the following marketplace{quoteRequestErrors.length === 1 ? '' : 's'} was unsuccessful on the latest quote reqeust
                                            </AlertDescription>
                                            <VStack align="stretch" spacing={0.5} mt={1}>
                                                {quoteRequestErrors.map(({ marketplace, error }) => (
                                                    <Text key={marketplace} color="gray.600" fontSize="xs">
                                                        <Text as="span" fontWeight="semibold">
                                                            {SOURCE_MARKETPLACE_CONFIG[marketplace]?.label ?? marketplace}
                                                        </Text>
                                                        {`: ${error}`}
                                                    </Text>
                                                ))}
                                            </VStack>
                                        </Box>
                                    </Alert>
                                )}

                                {tab.key === 'pending' && potentialTotal && quoteRequestAllowedAutoOrder && (
                                    <Alert
                                        status="error"
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="red.400"
                                        bg="red.50"
                                    >
                                        <AlertIcon color="red.500" />
                                        <AlertDescription color="gray.700" fontSize="sm">
                                            Auto-ordering was disabled because the total parts value increased by 50%.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                                    <Selection
                                        key={tab.key}
                                        sectionKey={tab.key}
                                        parts={tab.parts}
                                        orderId={orderId}
                                        vin={vin}
                                        refetch={refetch}
                                        filter='estimate'
                                        isEvOrder={isEvOrder}
                                        canAttemptPnChange={canAttemptPnChange}
                                        quotesOnly={quotesOnly}
                                        potentialTotal={potentialTotal}
                                        searchQuery={searchQuery}
                                        drpJob={drpJob}
                                    />
                                </Box>
                            </VStack>
                        </TabPanel>
                    ))}
                </TabPanels>
            </Tabs>
        </Box>
    );
};

export default SelectionTabs;
